import React, { useState } from 'react';
import { PieChart, Coins, Lock, TrendingUp, ChevronDown, HelpCircle, FileText, Download } from 'lucide-react';
import { useLang } from '../LangContext';
import { whitepaperUrl } from '../mock';

const tokenAllocation = [
  { label: 'Public Sale', value: 40, amount: '400M', color: '#3b82f6' },
  { label: 'Ecosystem & Staking', value: 21.5, amount: '215M', color: '#f59e0b' },
  { label: 'Liquidity', value: 20, amount: '200M', color: '#60a5fa' },
  { label: 'Marketing', value: 10, amount: '100M', color: '#10b981' },
  { label: 'Team & Advisors', value: 5, amount: '50M', color: '#a855f7' },
  { label: 'Community Airdrop', value: 3.5, amount: '35M', color: '#ef4444' },
];

const stakingPlans = [
  {
    duration: '3 Months',
    durationFr: '3 Mois',
    durationEs: '3 Meses',
    apy: '8%',
    min: '1,000 DOMIA',
    perks: {
      en: ['Staker Badge'],
      fr: ['Badge Staker'],
      es: ['Insignia Staker'],
    },
  },
  {
    duration: '6 Months',
    durationFr: '6 Mois',
    durationEs: '6 Meses',
    apy: '15%',
    min: '2,500 DOMIA',
    perks: {
      en: ['1 free VIP tournament ticket per month', 'Access to exclusive Domino tables', 'Rare skins unlocked'],
      fr: ['1 ticket de tournoi VIP gratuit par mois', 'Accès aux tables Domino exclusives', 'Skins rares débloqués'],
      es: ['1 entrada de torneo VIP gratis por mes', 'Acceso a mesas Domino exclusivas', 'Skins raros desbloqueados'],
    },
  },
  {
    duration: '9 Months',
    durationFr: '9 Mois',
    durationEs: '9 Meses',
    apy: '22%',
    min: '5,000 DOMIA',
    perks: {
      en: ['Full VIP access to high-stakes cash tournaments', 'Double voting power in governance decisions'],
      fr: ['Accès VIP complet aux tournois cash high-stakes', 'Pouvoir de vote double dans les décisions de gouvernance'],
      es: ['Acceso VIP completo a torneos cash de altas apuestas', 'Doble poder de voto en decisiones de gobernanza'],
    },
    highlight: true,
  },
  {
    duration: '12 Months',
    durationFr: '12 Mois',
    durationEs: '12 Meses',
    apy: '30%',
    min: '10,000 DOMIA',
    perks: {
      en: ['Permanent "VIP Gold" status', 'Elite Legend Badge', 'Free entry to the Annual Grand Tournament', 'Top priority for future airdrops'],
      fr: ['Statut "VIP Gold" permanent', 'Badge Elite Légende', 'Entrée gratuite au Grand Tournoi Annuel', 'Priorité absolue sur les futurs airdrops'],
      es: ['Estado "VIP Gold" permanente', 'Insignia Élite Leyenda', 'Entrada gratis al Gran Torneo Anual', 'Máxima prioridad en futuros airdrops'],
    },
    premium: true,
  },
];

const faqContent = {
  en: [
    { q: 'What is Domino Arena?', a: 'Domino Arena is a next-generation Play-to-Earn gaming platform built on the Solana blockchain, combining classic board games with decentralized finance.' },
    { q: 'How can I buy DOMIA tokens?', a: 'You can participate in our ongoing presale by depositing USDC or USDT directly from the Presale section above. Multiple chains are supported (Solana, Polygon, TRC20, BEP20).' },
    { q: 'When will DOMIA be listed on exchanges?', a: 'DOMIA will be listed on major DEX and CEX platforms shortly after the presale ends. The listing price range will be $0.0030 – $0.0040.' },
    { q: 'Is Domino Arena safe and audited?', a: 'Yes. All smart contracts are audited by third-party security firms, and every transaction is fully traceable on the Solana blockchain.' },
    { q: 'How does Play-to-Earn work?', a: 'Win matches in any of our games (Domino, Ludo, Chess, etc.) to earn DOMIA tokens. You can withdraw them as USDT or reinvest in tournaments.' },
    { q: 'How can I become a partner or sponsor?', a: 'Contact our team via our official Telegram or X (Twitter) channels available in the Community section.' },
  ],
  fr: [
    { q: 'Qu\'est-ce que Domino Arena ?', a: 'Domino Arena est une plateforme de jeux Play-to-Earn de nouvelle génération construite sur la blockchain Solana, combinant jeux de société classiques et finance décentralisée.' },
    { q: 'Comment acheter des tokens DOMIA ?', a: 'Vous pouvez participer à notre prévente en cours en déposant des USDC ou USDT directement depuis la section Prévente ci-dessus. Plusieurs chaînes sont supportées (Solana, Polygon, TRC20, BEP20).' },
    { q: 'Quand DOMIA sera listé sur les exchanges ?', a: 'DOMIA sera listé sur les principales plateformes DEX et CEX peu après la fin de la prévente. La fourchette de prix de cotation sera de 0,0030 $ à 0,0040 $.' },
    { q: 'Domino Arena est-il sûr et audité ?', a: 'Oui. Tous les contrats intelligents sont audités par des firmes de sécurité tierces, et chaque transaction est entièrement traçable sur la blockchain Solana.' },
    { q: 'Comment fonctionne le Play-to-Earn ?', a: 'Gagnez des matchs dans nos jeux (Domino, Ludo, Échecs, etc.) pour gagner des tokens DOMIA. Vous pouvez les retirer en USDT ou les réinvestir dans des tournois.' },
    { q: 'Comment devenir partenaire ou sponsor ?', a: 'Contactez notre équipe via nos canaux officiels Telegram ou X (Twitter) disponibles dans la section Communauté.' },
  ],
  es: [
    { q: '¿Qué es Domino Arena?', a: 'Domino Arena es una plataforma de juegos Play-to-Earn de próxima generación construida en la blockchain de Solana, que combina juegos de mesa clásicos con finanzas descentralizadas.' },
    { q: '¿Cómo puedo comprar tokens DOMIA?', a: 'Puedes participar en nuestra preventa en curso depositando USDC o USDT directamente desde la sección Preventa de arriba. Se admiten varias cadenas (Solana, Polygon, TRC20, BEP20).' },
    { q: '¿Cuándo se listará DOMIA en los exchanges?', a: 'DOMIA se listará en las principales plataformas DEX y CEX poco después del final de la preventa. El rango de precio de cotización será de $0.0030 a $0.0040.' },
    { q: '¿Es seguro y auditado Domino Arena?', a: 'Sí. Todos los contratos inteligentes son auditados por firmas de seguridad externas, y cada transacción es completamente rastreable en la blockchain de Solana.' },
    { q: '¿Cómo funciona el Play-to-Earn?', a: 'Gana partidas en nuestros juegos (Domino, Ludo, Ajedrez, etc.) para ganar tokens DOMIA. Puedes retirarlos como USDT o reinvertirlos en torneos.' },
    { q: '¿Cómo puedo ser socio o patrocinador?', a: 'Contacta a nuestro equipo a través de nuestros canales oficiales de Telegram o X (Twitter) disponibles en la sección Comunidad.' },
  ],
};

const sectionLabels = {
  en: {
    tokTitle: 'TOKENOMICS', tokSub: 'DOMIA Token Allocation', tokTotal: 'Total Supply',
    stakeTitle: 'STAKING', stakeSub: 'Lock your DOMIA and earn passive rewards', stakeBtn: 'Coming Soon',
    duration: 'Duration', apy: 'APY', min: 'Min. Stake', perks: 'Perks',
    faqTitle: 'FAQ', faqSub: 'Frequently Asked Questions',
    wpTitle: 'WHITEPAPER', wpSub: 'Read or download the full Domino Arena Whitepaper to discover our vision, technical architecture, tokenomics and roadmap.', wpBtn: 'Download Whitepaper (PDF)',
  },
  fr: {
    tokTitle: 'TOKENOMICS', tokSub: 'Répartition du Token DOMIA', tokTotal: 'Offre Totale',
    stakeTitle: 'STAKING', stakeSub: 'Bloquez vos DOMIA et gagnez des récompenses passives', stakeBtn: 'Bientôt Disponible',
    duration: 'Durée', apy: 'APY', min: 'Mise Min.', perks: 'Avantages',
    faqTitle: 'FAQ', faqSub: 'Questions Fréquemment Posées',
    wpTitle: 'LIVRE BLANC', wpSub: 'Lisez ou téléchargez l\'intégralité du Livre Blanc Domino Arena pour découvrir notre vision, l\'architecture technique, la tokenomics et la feuille de route.', wpBtn: 'Télécharger le Livre Blanc (PDF)',
  },
  es: {
    tokTitle: 'TOKENOMICS', tokSub: 'Distribución del Token DOMIA', tokTotal: 'Suministro Total',
    stakeTitle: 'STAKING', stakeSub: 'Bloquea tus DOMIA y gana recompensas pasivas', stakeBtn: 'Próximamente',
    duration: 'Duración', apy: 'APY', min: 'Stake Mín.', perks: 'Beneficios',
    faqTitle: 'FAQ', faqSub: 'Preguntas Frecuentes',
    wpTitle: 'WHITEPAPER', wpSub: 'Lee o descarga el Whitepaper completo de Domino Arena para descubrir nuestra visión, arquitectura técnica, tokenomics y hoja de ruta.', wpBtn: 'Descargar Whitepaper (PDF)',
  },
};

// Interactive Donut chart using SVG arcs (hoverable + clickable)
const polarToCartesian = (cx, cy, r, angle) => {
  const a = (angle - 90) * Math.PI / 180;
  return { x: cx + r * Math.cos(a), y: cy + r * Math.sin(a) };
};

const arcPath = (cx, cy, rOuter, rInner, startAngle, endAngle) => {
  const startOuter = polarToCartesian(cx, cy, rOuter, endAngle);
  const endOuter = polarToCartesian(cx, cy, rOuter, startAngle);
  const startInner = polarToCartesian(cx, cy, rInner, startAngle);
  const endInner = polarToCartesian(cx, cy, rInner, endAngle);
  const large = endAngle - startAngle <= 180 ? 0 : 1;
  return [
    `M ${startOuter.x} ${startOuter.y}`,
    `A ${rOuter} ${rOuter} 0 ${large} 0 ${endOuter.x} ${endOuter.y}`,
    `L ${startInner.x} ${startInner.y}`,
    `A ${rInner} ${rInner} 0 ${large} 1 ${endInner.x} ${endInner.y}`,
    'Z',
  ].join(' ');
};

const DonutChart = ({ activeIdx, setActiveIdx }) => {
  const size = 240, cx = size / 2, cy = size / 2, rOuter = 110, rInner = 70;
  let acc = 0;
  const segments = tokenAllocation.map((s, i) => {
    const start = (acc / 100) * 360;
    acc += s.value;
    const end = (acc / 100) * 360;
    return { ...s, idx: i, startAngle: start, endAngle: end };
  });
  const active = activeIdx != null ? tokenAllocation[activeIdx] : null;
  return (
    <div className="relative mx-auto" style={{ width: size, height: size }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        {segments.map((seg) => {
          const isActive = activeIdx === seg.idx;
          const hover = activeIdx != null && activeIdx !== seg.idx;
          return (
            <path
              key={seg.idx}
              d={arcPath(cx, cy, isActive ? rOuter + 8 : rOuter, rInner, seg.startAngle, seg.endAngle)}
              fill={seg.color}
              opacity={hover ? 0.35 : 1}
              style={{ cursor: 'pointer', transition: 'all 250ms ease' }}
              onMouseEnter={() => setActiveIdx(seg.idx)}
              onMouseLeave={() => setActiveIdx(null)}
              onClick={() => setActiveIdx(activeIdx === seg.idx ? null : seg.idx)}
            />
          );
        })}
      </svg>
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
        <div className="text-center">
          {active ? (
            <>
              <div className="text-[10px] font-bold tracking-[0.14em] text-slate-500">{active.label.toUpperCase()}</div>
              <div className="text-3xl font-black text-slate-900 leading-tight">{active.value}%</div>
              <div className="text-[12px] font-extrabold" style={{ color: active.color }}>{active.amount}</div>
            </>
          ) : (
            <>
              <div className="text-[11px] font-bold text-slate-500">TOTAL</div>
              <div className="text-2xl font-black text-slate-900 leading-tight">1B</div>
              <div className="text-[11px] font-extrabold text-blue-600">DOMIA</div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const Sections = () => {
  const { lang, t } = useLang();
  const labels = sectionLabels[lang] || sectionLabels.en;
  const faqs = faqContent[lang] || faqContent.en;
  const [openFaq, setOpenFaq] = useState(null);
  const [activeTokenIdx, setActiveTokenIdx] = useState(null);

  return (
    <>
      {/* TOKENOMICS */}
      <section id="tokenomics" className="relative z-10 max-w-[1400px] mx-auto px-6 mt-16 scroll-mt-20">
        <div className="section-title">
          <div className="line" />
          <div className="pill"><PieChart className="w-4 h-4 text-blue-600" /> {labels.tokTitle}</div>
          <div className="line" />
        </div>
        <div className="glass-card p-6 md:p-8 grid grid-cols-1 md:grid-cols-[auto_1fr] gap-8 items-center">
          <DonutChart activeIdx={activeTokenIdx} setActiveIdx={setActiveTokenIdx} />
          <div>
            <h3 className="text-2xl font-black text-slate-900 tracking-tight">{labels.tokSub}</h3>
            <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-2.5">
              {tokenAllocation.map((a, i) => (
                <div
                  key={i}
                  onMouseEnter={() => setActiveTokenIdx(i)}
                  onMouseLeave={() => setActiveTokenIdx(null)}
                  onClick={() => setActiveTokenIdx(activeTokenIdx === i ? null : i)}
                  className={`flex items-center gap-3 rounded-xl px-3 py-2.5 cursor-pointer transition-all duration-200 ${activeTokenIdx === i ? 'bg-white border-2 shadow-[0_8px_22px_rgba(37,99,235,0.18)] scale-[1.02]' : 'bg-white/70 border border-slate-200 hover:bg-white'}`}
                  style={activeTokenIdx === i ? { borderColor: a.color } : {}}
                >
                  <span className="w-3.5 h-3.5 rounded-full shrink-0" style={{ background: a.color }} />
                  <span className="flex-1 text-[13px] font-semibold text-slate-700">{a.label}</span>
                  <span className="text-[13px] font-black text-slate-900">{a.value}%</span>
                  <span className="text-[12px] font-bold text-blue-600 min-w-[44px] text-right">{a.amount}</span>
                </div>
              ))}
            </div>
            <div className="mt-4 px-4 py-2.5 rounded-xl bg-gradient-to-r from-blue-50 to-amber-50 border border-blue-200 flex items-center justify-between">
              <span className="text-[12px] font-extrabold tracking-[0.14em] text-slate-700">{labels.tokTotal}</span>
              <span className="text-[14px] font-black text-slate-900">1,000,000,000 <span className="text-blue-600">$DOMIA</span></span>
            </div>
          </div>
        </div>
      </section>

      {/* STAKING */}
      <section id="staking" className="relative z-10 max-w-[1400px] mx-auto px-6 mt-16 scroll-mt-20">
        <div className="section-title">
          <div className="line" />
          <div className="pill"><Lock className="w-4 h-4 text-blue-600" /> {labels.stakeTitle}</div>
          <div className="line" />
        </div>
        <div className="glass-card p-6 md:p-8">
          <div className="flex items-start justify-between flex-wrap gap-4">
            <div>
              <h3 className="text-2xl font-black text-slate-900 tracking-tight flex items-center gap-2"><TrendingUp className="w-6 h-6 text-amber-500" /> {labels.stakeSub}</h3>
            </div>
            <span className="gold-pill">{labels.stakeBtn}</span>
          </div>
          <div className="mt-5 -mx-2 px-2 overflow-x-auto no-scrollbar" data-testid="staking-carousel">
            <div className="flex gap-3 snap-x snap-mandatory pb-2" style={{ scrollPaddingLeft: '8px' }}>
            {stakingPlans.map((p, i) => {
              const dur = lang === 'fr' ? p.durationFr : lang === 'es' ? p.durationEs : p.duration;
              const perks = p.perks[lang] || p.perks.en;
              return (
                <div key={i} className={`relative snap-start shrink-0 w-[78vw] sm:w-[300px] rounded-2xl p-5 border transition-all duration-300 hover:-translate-y-1 ${
                  p.premium
                    ? 'border-amber-300 bg-gradient-to-br from-amber-50 via-white to-blue-50 hover:shadow-[0_16px_36px_rgba(245,158,11,0.25)]'
                    : p.highlight
                      ? 'border-blue-300 bg-gradient-to-br from-blue-50 to-white hover:shadow-[0_14px_30px_rgba(37,99,235,0.22)]'
                      : 'border-blue-200 bg-gradient-to-br from-white to-blue-50 hover:shadow-[0_12px_28px_rgba(37,99,235,0.18)]'
                }`} data-testid={`staking-plan-${i}`}>
                  {p.premium && (
                    <span className="absolute -top-2.5 right-3 gold-pill text-[10px]">VIP GOLD</span>
                  )}
                  <div className="text-[11px] font-bold tracking-[0.18em] text-slate-500">{labels.duration}</div>
                  <div className="text-xl font-black text-slate-900 mt-1">{dur}</div>
                  <div className="mt-3 flex items-end gap-2">
                    <span className={`text-4xl font-black bg-clip-text text-transparent ${p.premium ? 'bg-gradient-to-r from-amber-600 to-amber-400' : 'bg-gradient-to-r from-blue-700 to-blue-400'}`}>{p.apy}</span>
                    <span className="text-[11px] font-bold text-slate-500 pb-1.5">{labels.apy}</span>
                  </div>
                  <div className="mt-2 text-[12px] font-semibold text-slate-600 flex items-center gap-1.5">
                    <Coins className="w-3.5 h-3.5 text-amber-500" />
                    {labels.min}: <span className="font-black text-slate-900">{p.min}</span>
                  </div>
                  <div className="mt-3 pt-3 border-t border-blue-100">
                    <div className="text-[10px] font-extrabold tracking-[0.14em] text-blue-600 mb-1.5">{labels.perks}</div>
                    <ul className="space-y-1">
                      {perks.map((perk, idx) => (
                        <li key={idx} className="flex items-start gap-1.5 text-[11.5px] text-slate-700 leading-snug">
                          <span className={`w-1.5 h-1.5 rounded-full mt-1.5 shrink-0 ${p.premium ? 'bg-amber-500' : 'bg-blue-500'}`} />
                          <span>{perk}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              );
            })}
            </div>
            <div className="mt-2 flex items-center justify-center gap-1 text-[10px] font-bold text-slate-400 tracking-widest uppercase sm:hidden">
              ← Swipe →
            </div>
          </div>
        </div>
      </section>

      {/* WHITEPAPER (also serves as Roadmap link since user said Roadmap is inside whitepaper) */}
      <section id="whitepaper" className="relative z-10 max-w-[1400px] mx-auto px-6 mt-16 scroll-mt-20">
        <div className="section-title">
          <div className="line" />
          <div className="pill"><FileText className="w-4 h-4 text-blue-600" /> {labels.wpTitle}</div>
          <div className="line" />
        </div>
        <div id="roadmap" className="glass-card p-8 text-center scroll-mt-20">
          <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-700 to-blue-500 flex items-center justify-center mx-auto shadow-lg">
            <FileText className="w-8 h-8 text-white" />
          </div>
          <p className="mt-4 max-w-2xl mx-auto text-slate-600 leading-relaxed">{labels.wpSub}</p>
          <a
            href={whitepaperUrl}
            target="_blank"
            rel="noopener noreferrer"
            download
            className="mt-6 inline-flex items-center gap-2 px-7 py-3.5 rounded-full font-extrabold text-white text-[14px] shadow-[0_10px_24px_rgba(37,99,235,0.45)] hover:translate-y-[-1px] transition-transform"
            style={{ background: 'linear-gradient(90deg, #1d4ed8, #3b82f6)' }}
          >
            <Download className="w-5 h-5" />
            {labels.wpBtn}
          </a>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="relative z-10 max-w-[1400px] mx-auto px-6 mt-16 scroll-mt-20">
        <div className="section-title">
          <div className="line" />
          <div className="pill"><HelpCircle className="w-4 h-4 text-blue-600" /> {labels.faqTitle}</div>
          <div className="line" />
        </div>
        <div className="glass-card p-4 md:p-6 max-w-3xl mx-auto">
          <h3 className="text-xl font-black text-slate-900 tracking-tight px-2 mb-3">{labels.faqSub}</h3>
          <div className="space-y-2">
            {faqs.map((f, i) => (
              <div key={i} className={`rounded-2xl border transition-all duration-300 ${openFaq === i ? 'border-blue-300 bg-gradient-to-br from-blue-50 to-white shadow-[0_8px_22px_rgba(37,99,235,0.12)]' : 'border-slate-200 bg-white/70'}`}>
                <button onClick={() => setOpenFaq(openFaq === i ? null : i)} className="w-full flex items-center justify-between gap-3 px-4 py-3.5 text-left">
                  <span className="font-bold text-slate-900 text-[14px]">{f.q}</span>
                  <ChevronDown className={`w-4 h-4 text-slate-500 transition-transform duration-300 ${openFaq === i ? 'rotate-180 text-blue-600' : ''}`} />
                </button>
                <div className="overflow-hidden transition-all duration-300" style={{ maxHeight: openFaq === i ? '300px' : '0px', opacity: openFaq === i ? 1 : 0 }}>
                  <p className="px-4 pb-4 pt-1 text-[13px] text-slate-600 leading-relaxed border-t border-blue-100">{f.a}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
};

export default Sections;
